import { Section, CodeBlock, InfoBox } from "@/components"

export function ReactShadcnComponentsPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Components</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Explore the UI components included in the template and learn how to use and customize 
          them. The template uses Shadcn UI for accessible, customizable components.
        </p>
      </div>

      <Section title="Component Organization" description="How components are structured">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-card border rounded-lg p-6 space-y-3">
            <h3 className="font-semibold">ui/</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Shadcn UI components installed directly in your project. Fully customizable and themed.
            </p>
          </div>
          <div className="bg-card border rounded-lg p-6 space-y-3">
            <h3 className="font-semibold">common/</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Custom reusable components built specifically for this template's needs.
            </p>
          </div>
          <div className="bg-card border rounded-lg p-6 space-y-3">
            <h3 className="font-semibold">layout/</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Layout-specific components like Sidebar, Header, and Navigation.
            </p>
          </div>
        </div>
      </Section>

      <Section title="Common Components" description="Custom built components">
        <div className="space-y-8">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">StatCard</h3>
            <p className="text-muted-foreground">
              Display statistics and metrics with optional trend indicators.
            </p>
            <CodeBlock
              code={`import { StatCard } from "@/components/common/StatCard"
import { Users, TrendingUp } from "lucide-react"

function Dashboard() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <StatCard
        title="Total Users"
        value="2,543"
        icon={<Users className="h-5 w-5" />}
        trend={{ value: 12, isPositive: true }}
        description="Active users this month"
      />
    </div>
  )
}`}
              language="typescript"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold">LoadingSpinner</h3>
            <p className="text-muted-foreground">
              Show loading states with optional text and size variants.
            </p>
            <CodeBlock
              code={`import { LoadingSpinner } from "@/components/common/LoadingSpinner"

function MyComponent() {
  const [loading, setLoading] = useState(true)

  if (loading) {
    return <LoadingSpinner text="Loading data..." />
  }

  return <div>Content</div>
}`}
              language="typescript"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold">ErrorMessage</h3>
            <p className="text-muted-foreground">
              Display error states with optional retry functionality.
            </p>
            <CodeBlock
              code={`import { ErrorMessage } from "@/components/common/ErrorMessage"

function MyComponent() {
  const [error, setError] = useState<string | null>(null)

  const handleRetry = () => {
    // Retry logic
    fetchData()
  }

  if (error) {
    return (
      <ErrorMessage
        message={error}
        onRetry={handleRetry}
      />
    )
  }

  return <div>Content</div>
}`}
              language="typescript"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold">EmptyState</h3>
            <p className="text-muted-foreground">
              Show empty states with icons and call-to-action buttons.
            </p>
            <CodeBlock
              code={`import { EmptyState } from "@/components/common/EmptyState"
import { Users } from "lucide-react"

function UsersList() {
  const users = []

  if (users.length === 0) {
    return (
      <EmptyState
        icon={<Users className="h-12 w-12" />}
        title="No users found"
        description="Get started by creating your first user"
        action={{
          label: "Add User",
          onClick: () => navigate('/users/new')
        }}
      />
    )
  }

  return <div>Users list...</div>
}`}
              language="typescript"
            />
          </div>
        </div>
      </Section>

      <Section title="Layout Components" description="Navigation and structure">
        <div className="space-y-8">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Sidebar</h3>
            <p className="text-muted-foreground">
              Responsive sidebar navigation with collapsible menu and active state indicators.
            </p>
            <CodeBlock
              code={`import { Sidebar } from "@/components/layout/Sidebar"
import { LayoutDashboard, Users, Settings } from "lucide-react"

const menuItems = [
  { icon: <LayoutDashboard />, label: "Dashboard", path: "/dashboard" },
  { icon: <Users />, label: "Users", path: "/users" },
  { icon: <Settings />, label: "Settings", path: "/settings" }
]

export function AppLayout() {
  return (
    <div className="flex">
      <Sidebar items={menuItems} />
      <main className="flex-1">
        {/* Content */}
      </main>
    </div>
  )
}`}
              language="typescript"
            />
            <InfoBox type="info" title="Features">
              The sidebar includes collapse/expand functionality, active link highlighting, 
              and responsive behavior for mobile devices.
            </InfoBox>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Header</h3>
            <p className="text-muted-foreground">
              Page header with breadcrumbs, search, notifications, and user menu.
            </p>
            <CodeBlock
              code={`import { Header } from "@/components/layout/Header"

export function AppLayout() {
  return (
    <div className="flex flex-col h-screen">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-y-auto p-6">
          {/* Content */}
        </main>
      </div>
    </div>
  )
}`}
              language="typescript"
            />
          </div>
        </div>
      </Section>

      <Section title="Shadcn UI Components" description="Pre-installed components">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The template comes with commonly used Shadcn UI components. All components are 
            located in <code className="bg-muted px-2 py-1 rounded">src/components/ui/</code> 
            and can be customized as needed.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ComponentListCard
              title="Form Elements"
              items={[
                "Button",
                "Input",
                "Select",
                "Checkbox",
                "Radio Group",
                "Switch",
                "Textarea",
                "Label"
              ]}
            />
            <ComponentListCard
              title="Data Display"
              items={[
                "Card",
                "Table",
                "Badge",
                "Avatar",
                "Separator",
                "Skeleton"
              ]}
            />
            <ComponentListCard
              title="Overlays"
              items={[
                "Dialog",
                "Sheet",
                "Dropdown Menu",
                "Tooltip",
                "Popover",
                "Toast"
              ]}
            />
            <ComponentListCard
              title="Navigation"
              items={[
                "Tabs",
                "Breadcrumb",
                "Navigation Menu",
                "Command"
              ]}
            />
            <ComponentListCard
              title="Feedback"
              items={[
                "Alert",
                "Progress",
                "Spinner",
                "Toast"
              ]}
            />
            <ComponentListCard
              title="Utilities"
              items={[
                "Scroll Area",
                "Collapsible",
                "Accordion",
                "Aspect Ratio"
              ]}
            />
          </div>
        </div>
      </Section>

      <Section title="Adding New Components" description="Extend your component library">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Shadcn UI makes it easy to add new components as your project grows. Components 
            are installed directly into your project, giving you full control.
          </p>
          
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Install a single component</h3>
            <CodeBlock
              code={`npx shadcn-ui@latest add calendar`}
              language="bash"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Install multiple components</h3>
            <CodeBlock
              code={`npx shadcn-ui@latest add calendar date-picker form`}
              language="bash"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">View all available components</h3>
            <CodeBlock
              code={`npx shadcn-ui@latest add`}
              language="bash"
            />
            <p className="text-sm text-muted-foreground">
              This will show an interactive list of all available components.
            </p>
          </div>

          <InfoBox type="info" title="Customization">
            After adding a component, you'll find it in <code className="bg-muted px-2 py-1 rounded">src/components/ui/</code>. 
            You can modify the component code directly to match your design needs.
          </InfoBox>
        </div>
      </Section>

      <Section title="Component Example" description="Building a custom component">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Here's an example of building a custom component that combines Shadcn UI primitives:
          </p>
          
          <CodeBlock
            code={`import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { MoreVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface UserCardProps {
  user: {
    name: string
    email: string
    role: string
    status: "active" | "inactive"
  }
  onEdit: () => void
  onDelete: () => void
}

export function UserCard({ user, onEdit, onDelete }: UserCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg font-medium">{user.name}</CardTitle>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>Edit</DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-destructive">
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-2">{user.email}</p>
        <div className="flex gap-2">
          <Badge variant="outline">{user.role}</Badge>
          <Badge variant={user.status === "active" ? "default" : "secondary"}>
            {user.status}
          </Badge>
        </div>
      </CardContent>
    </Card>
  )
}`}
            language="typescript"
            filename="src/components/common/UserCard.tsx"
          />
        </div>
      </Section>

      <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
        <h2 className="text-2xl font-bold">Component Best Practices</h2>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Keep components small and focused on a single responsibility</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use TypeScript interfaces to define component props clearly</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Compose complex components from simpler ones</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Extract reusable logic into custom hooks</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use Shadcn UI components as building blocks for custom components</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Maintain consistent spacing and sizing using Tailwind classes</span>
          </li>
        </ul>
      </div>
    </div>
  )
}

function ComponentListCard({ 
  title, 
  items 
}: { 
  title: string
  items: string[]
}) {
  return (
    <div className="bg-card border rounded-lg p-4 space-y-3">
      <h4 className="font-semibold">{title}</h4>
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={index} className="text-sm text-muted-foreground flex gap-2">
            <span className="text-primary">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
